import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import axios from "axios";

import * as XLSX from "xlsx";

import {
  FaCalendarAlt,
  FaChevronRight,
  FaFileExcel,
  FaMoneyBillWave,
  FaSync,
  FaUsers,
} from "react-icons/fa";


/* ============================================================
   API
============================================================ */

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://dgps.co.in";


const MonthlyDueReportPage = () => {

  const navigate =
    useNavigate();


  /* ============================================================
     STATE
  ============================================================ */

  const [reportData, setReportData] =
    useState({

      academic_year: null,

      monthly_payable: 0,

      months: [],

      total_due: 0,

      total_collected: 0,
    });


  const [loading, setLoading] =
    useState(true);


  const [error, setError] =
    useState("");


  /* ============================================================
     HEADERS
  ============================================================ */

  const getHeaders = () => {

    const token =

      localStorage.getItem("token") ||

      localStorage.getItem(
        "auth_token"
      ) ||

      localStorage.getItem(
        "access_token"
      ) ||

      "";


    const schoolId =

      localStorage.getItem(
        "school_id"
      ) ||

      localStorage.getItem(
        "schoolId"
      ) ||

      localStorage.getItem(
        "selected_school_id"
      ) ||

      "";


    return {

      Authorization:
        token
          ? `Bearer ${token}`
          : "",

      "X-School-Id":
        schoolId,

      Accept:
        "application/json",
    };
  };


  /* ============================================================
     MONEY
  ============================================================ */

  const money = (amount) => {

    return new Intl.NumberFormat(
      "en-IN",
      {

        style:
          "currency",

        currency:
          "INR",

        minimumFractionDigits:
          0,

        maximumFractionDigits:
          2,
      }
    ).format(
      Number(amount || 0)
    );
  };


  /* ============================================================
     LOAD REPORT
  ============================================================ */

  const loadReport = async () => {

    try {

      setLoading(true);

      setError("");


      const response =
        await axios.get(

          `${API_BASE_URL}/api/reports/fee-due/monthly-summary`,

          {

            headers:
              getHeaders(),
          }
        );


      console.log(
        "MONTHLY DUE RESPONSE:",
        response.data
      );


      if (
        response?.data?.status
      ) {

        const data =
          response.data.data ||
          {};


        setReportData({

          academic_year:

            data.academic_year ||
            null,


          monthly_payable:

            Number(
              data.monthly_payable ||
              0
            ),


          months:

            Array.isArray(
              data.months
            )

              ? data.months

              : [],


          total_due:

            Number(
              data.total_due ||
              0
            ),


          total_collected:

            Number(
              data.total_collected ||
              0
            ),
        });

      } else {

        setReportData({

          academic_year:
            null,

          monthly_payable:
            0,

          months:
            [],

          total_due:
            0,

          total_collected:
            0,
        });


        setError(

          response?.data?.message ||

          "Unable to load monthly due report."
        );
      }

    } catch (err) {

      console.error(
        "Monthly Due Report Error:",
        err
      );


      setReportData({

        academic_year:
          null,

        monthly_payable:
          0,

        months:
          [],

        total_due:
          0,

        total_collected:
          0,
      });


      setError(

        err?.response?.data
          ?.message ||

        "Unable to load monthly due report."
      );

    } finally {

      setLoading(false);
    }
  };


  /* ============================================================
     FIRST LOAD
  ============================================================ */

  useEffect(() => {

    loadReport();

  }, []);


  /* ============================================================
     MONTHS
  ============================================================ */

  const months =
    useMemo(() => {

      return Array.isArray(
        reportData.months
      )

        ? reportData.months

        : [];

    }, [
      reportData.months,
    ]);


  /* ============================================================
     ACCRUED MONTHS

     Future month GRAND TOTAL-এ যাবে না।
  ============================================================ */

  const accruedMonths =
    useMemo(() => {

      return months.filter(
        (row) =>
          row.is_accrued === true
      );

    }, [
      months,
    ]);


  /* ============================================================
     HIGHEST DUE STUDENTS
  ============================================================ */

  const highestDueStudents =
    useMemo(() => {

      if (
        !accruedMonths.length
      ) {

        return 0;
      }


      return Math.max(

        ...accruedMonths.map(

          (row) =>

            Number(
              row.due_students ||
              0
            )
        )
      );

    }, [
      accruedMonths,
    ]);


  /* ============================================================
     GRAND TOTAL PAYABLE

     Jan থেকে current month পর্যন্ত।
     Future month add হবে না।
  ============================================================ */

  const grandTotalPayable =
    useMemo(() => {

      return accruedMonths.reduce(
        (
          total,
          row
        ) => {

          return (

            total +

            Number(
              row.total_payable ||
              0
            )
          );

        },
        0
      );

    }, [
      accruedMonths,
    ]);


  /* ============================================================
     GRAND TOTAL COLLECTED
  ============================================================ */

  const grandTotalCollected =
    useMemo(() => {

      return accruedMonths.reduce(
        (
          total,
          row
        ) => {

          return (

            total +

            Number(
              row.total_paid ||
              0
            )
          );

        },
        0
      );

    }, [
      accruedMonths,
    ]);


  /* ============================================================
     GRAND TOTAL DUE

     Jan due
     + Feb due
     + Mar due
     ...
     + Current month due

     Previous due কোনো মাসে repeat হচ্ছে না।
  ============================================================ */

  const grandTotalDue =
    useMemo(() => {

      return accruedMonths.reduce(
        (
          total,
          row
        ) => {

          return (

            total +

            Number(
              row.due_amount ||
              0
            )
          );

        },
        0
      );

    }, [
      accruedMonths,
    ]);


  /* ============================================================
     OPEN MONTH
  ============================================================ */

  const openMonth = (row) => {

    if (
      !row?.month ||
      !row?.is_accrued
    ) {

      return;
    }


    navigate(

      `/admin/reports/monthly-due/${row.month}`,

      {

        state: {

          monthName:

            `${row.month_name} ${row.year}`,
        },
      }
    );
  };


  /* ============================================================
     EXCEL
  ============================================================ */

  const exportExcel = () => {

    if (!months.length) {

      return;
    }


    const excelRows =

      months.map(
        (
          row,
          index
        ) => ({

          "#":
            index + 1,


          "Month":

            `${row.month_name} ${row.year}`,


          "Due Students":

            row.is_accrued

              ? Number(
                  row.due_students ||
                  0
                )

              : 0,


          "Total Payable":

            Number(
              row.total_payable ||
              0
            ),


          "Total Collected":

            row.is_accrued

              ? Number(
                  row.total_paid ||
                  0
                )

              : 0,


          "Due Amount":

            row.is_accrued

              ? Number(
                  row.due_amount ||
                  0
                )

              : 0,


          "Status":

            row.is_accrued

              ? "Calculated"

              : "Upcoming",
        })
      );


    /* GRAND TOTAL */

    excelRows.push({

      "#":
        "",


      "Month":
        "GRAND TOTAL",


      "Due Students":
        "",


      "Total Payable":
        grandTotalPayable,


      "Total Collected":
        grandTotalCollected,


      "Due Amount":
        grandTotalDue,


      "Status":
        "",
    });


    const worksheet =

      XLSX.utils
        .json_to_sheet(
          excelRows
        );


    worksheet["!cols"] = [

      {
        wch: 6,
      },

      {
        wch: 22,
      },

      {
        wch: 16,
      },

      {
        wch: 20,
      },

      {
        wch: 20,
      },

      {
        wch: 20,
      },

      {
        wch: 15,
      },
    ];


    const workbook =

      XLSX.utils
        .book_new();


    XLSX.utils
      .book_append_sheet(

        workbook,

        worksheet,

        "Monthly Due"
      );


    const academicYear =

      String(

        reportData
          ?.academic_year
          ?.session_name ||

        "Academic_Year"

      ).replace(

        /[^a-zA-Z0-9_-]/g,

        "_"
      );


    XLSX.writeFile(

      workbook,

      `Monthly_Due_Report_${academicYear}.xlsx`
    );
  };


  /* ============================================================
     UI
  ============================================================ */

  return (

    <div className="md-page">


      {/* ========================================================
          PAGE HEADER
      ======================================================== */}

      <div className="md-page-header">


        <div className="md-heading-left">


          <div className="md-title-icon">

            <FaCalendarAlt />

          </div>


          <div>

            <h1>
              Monthly Due Report
            </h1>


            <p>
              Month-wise payable,
              collection and outstanding
            </p>

          </div>


        </div>


        <div className="md-top-actions">


          <button
            type="button"
            className="md-excel-btn"
            onClick={
              exportExcel
            }
            disabled={
              loading ||
              !months.length
            }
          >

            <FaFileExcel />

            Excel

          </button>


          <button
            type="button"
            className="md-refresh-btn"
            onClick={
              loadReport
            }
            disabled={
              loading
            }
          >

            <FaSync
              className={
                loading
                  ? "md-spin"
                  : ""
              }
            />

            Refresh

          </button>


        </div>


      </div>


      {/* ========================================================
          ACADEMIC YEAR
      ======================================================== */}

      {reportData
        .academic_year && (

        <div className="md-session-card">


          <div className="md-session-icon">

            <FaCalendarAlt />

          </div>


          <div>

            <span>
              Academic Year
            </span>


            <h3>

              {
                reportData
                  .academic_year
                  .session_name
              }

            </h3>

          </div>


          <div className="md-session-date">


            <span>

              {
                reportData
                  .academic_year
                  .start_date
              }

            </span>


            <b>
              →
            </b>


            <span>

              {
                reportData
                  .academic_year
                  .end_date
              }

            </span>


          </div>


        </div>

      )}


      {/* ========================================================
          TOP SUMMARY
      ======================================================== */}

      <div className="md-summary-grid">


        {/* DUE */}

        <div className="md-summary-card">


          <div className="md-summary-card-icon red">

            <FaMoneyBillWave />

          </div>


          <div>

            <span>
              Academic Year Due
            </span>


            <h2 className="summary-red-text">

              {money(
                grandTotalDue
              )}

            </h2>

          </div>


        </div>


        {/* MONTHLY PAYABLE */}

        <div className="md-summary-card">


          <div className="md-summary-card-icon blue">

            <FaCalendarAlt />

          </div>


          <div>

            <span>
              Monthly Payable
            </span>


            <h2>

              {money(
                reportData.monthly_payable
              )}

            </h2>

          </div>


        </div>


        {/* HIGHEST DUE STUDENTS */}

        <div className="md-summary-card">


          <div className="md-summary-card-icon purple">

            <FaUsers />

          </div>


          <div>

            <span>
              Highest Due Students
            </span>


            <h2>

              {
                highestDueStudents
              }

            </h2>

          </div>


        </div>


      </div>


      {/* ========================================================
          ERROR
      ======================================================== */}

      {error && (

        <div className="md-error">

          {error}

        </div>

      )}


      {/* ========================================================
          REPORT CARD
      ======================================================== */}

      <div className="md-report-card">


        {/* HEADER */}

        <div className="md-card-header">


          <div>

            <h2>
              Month Wise Outstanding
            </h2>


            <p>
              Each month shows only
              that month&apos;s payable,
              collection and due.
            </p>

          </div>


          <div className="md-header-total">

            <span>
              Current Outstanding
            </span>


            <strong>

              {money(
                grandTotalDue
              )}

            </strong>

          </div>


        </div>


        {/* ====================================================
            LOADING
        ==================================================== */}

        {loading ? (

          <div className="md-loading">


            <div className="md-loader" />


            <span>
              Loading monthly report...
            </span>


          </div>

        ) : (

          <div className="md-table-wrap">


            <table className="md-table">


              {/* =================================================
                  TABLE HEADER
              ================================================= */}

              <thead>

                <tr>


                  <th>
                    Month
                  </th>


                  <th className="text-center">
                    Due Students
                  </th>


                  <th className="text-right">
                    Total Payable
                  </th>


                  <th className="text-right">
                    Total Collected
                  </th>


                  <th className="text-right">
                    Due Amount
                  </th>


                  <th className="text-center">
                    Action
                  </th>


                </tr>

              </thead>


              {/* =================================================
                  TABLE BODY
              ================================================= */}

              <tbody>


                {!months.length ? (

                  <tr>

                    <td
                      colSpan="6"
                      className="md-empty"
                    >

                      No monthly report found.

                    </td>

                  </tr>

                ) : (

                  months.map(
                    (row) => {


                      const isFuture =
                        !row.is_accrued;


                      return (

                        <tr
                          key={
                            row.month
                          }
                          className={
                            isFuture
                              ? "md-future-row"
                              : ""
                          }
                        >


                          {/* MONTH */}

                          <td>

                            <div className="md-month">


                              <div className="md-month-box">

                                {
                                  row.month_short
                                }

                              </div>


                              <div>

                                <strong>

                                  {
                                    row.month_name
                                  }

                                </strong>


                                <span>

                                  {
                                    row.year
                                  }

                                </span>

                              </div>


                            </div>

                          </td>


                          {/* DUE STUDENTS */}

                          <td className="text-center">


                            {isFuture ? (

                              <span className="md-upcoming">

                                Upcoming

                              </span>

                            ) : (

                              <span className="md-student-badge">

                                <FaUsers />

                                {
                                  row.due_students ||
                                  0
                                }

                              </span>

                            )}


                          </td>


                          {/* TOTAL PAYABLE */}

                          <td className="text-right">

                            <strong className="md-payable-value">

                              {money(
                                row.total_payable
                              )}

                            </strong>

                          </td>


                          {/* TOTAL COLLECTED */}

                          <td className="text-right">

                            <strong
                              className={
                                isFuture
                                  ? "md-zero"
                                  : "md-collected-value"
                              }
                            >

                              {money(
                                isFuture
                                  ? 0
                                  : row.total_paid
                              )}

                            </strong>

                          </td>


                          {/* DUE */}

                          <td className="text-right">

                            <strong
                              className={
                                isFuture ||
                                Number(
                                  row.due_amount ||
                                  0
                                ) <= 0

                                  ? "md-zero"

                                  : "md-due-amount"
                              }
                            >

                              {money(
                                isFuture
                                  ? 0
                                  : row.due_amount
                              )}

                            </strong>

                          </td>


                          {/* VIEW */}

                          <td className="text-center">

                            <button
                              type="button"
                              className="md-view-btn"
                              disabled={
                                isFuture
                              }
                              onClick={() =>
                                openMonth(
                                  row
                                )
                              }
                            >

                              View

                              <FaChevronRight />

                            </button>

                          </td>


                        </tr>

                      );
                    }
                  )

                )}


              </tbody>


              {/* =================================================
                  GRAND TOTAL

                  EXACTLY TABLE LINE-ER NICHE
              ================================================= */}

              {!!months.length && (

                <tfoot>


                  <tr className="md-grand-total-row">


                    {/* GRAND TOTAL LABEL */}

                    <td
                      colSpan="2"
                      className="md-grand-title"
                    >

                      GRAND TOTAL

                      <small>

                        Current month 

                      </small>

                    </td>


                    {/* TOTAL PAYABLE */}

                    <td className="text-right">

                      <span className="grand-label-mobile">

                        PAYABLE

                      </span>

                      <strong className="grand-payable-value">

                        {money(
                          grandTotalPayable
                        )}

                      </strong>

                    </td>


                    {/* TOTAL COLLECTED */}

                    <td className="text-right">

                      <span className="grand-label-mobile">

                        COLLECTED

                      </span>

                      <strong className="grand-collected-value">

                        {money(
                          grandTotalCollected
                        )}

                      </strong>

                    </td>


                    {/* TOTAL DUE */}

                    <td className="text-right">

                      <span className="grand-label-mobile">

                        DUE

                      </span>

                      <strong className="grand-due-value">

                        {money(
                          grandTotalDue
                        )}

                      </strong>

                    </td>


                    {/* ACTION BLANK */}

                    <td />

                  </tr>


                </tfoot>

              )}


            </table>


          </div>

        )}


      </div>


      {/* ========================================================
          INLINE CSS
      ======================================================== */}

      <style>{`

        /* ====================================================
           PAGE
        ==================================================== */

        .md-page {
          width: 100%;
          min-height: 100vh;

          padding: 24px;

          box-sizing: border-box;

          background: #f5f8fc;

          font-family:
            Inter,
            "Segoe UI",
            sans-serif;
        }


        /* ====================================================
           HEADER
        ==================================================== */

        .md-page-header {
          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 18px;

          margin-bottom: 20px;
        }


        .md-heading-left {
          display: flex;

          align-items: center;

          gap: 14px;
        }


        .md-title-icon {
          width: 48px;
          height: 48px;

          flex: 0 0 48px;

          display: grid;

          place-items: center;

          border-radius: 14px;

          background:
            linear-gradient(
              135deg,
              #0f766e,
              #0891b2
            );

          color: #ffffff;

          font-size: 20px;

          box-shadow:
            0 8px 20px
            rgba(
              8,
              145,
              178,
              0.18
            );
        }


        .md-page-header h1 {
          margin: 0;

          color: #102a43;

          font-size: 27px;

          font-weight: 850;
        }


        .md-page-header p {
          margin: 5px 0 0;

          color: #829ab1;

          font-size: 12px;
        }


        .md-top-actions {
          display: flex;

          align-items: center;

          gap: 9px;
        }


        /* ====================================================
           BUTTONS
        ==================================================== */

        .md-refresh-btn,
        .md-excel-btn {
          min-height: 40px;

          border: 0;

          outline: none;

          padding: 0 15px;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 8px;

          border-radius: 9px;

          cursor: pointer;

          font-size: 11px;

          font-weight: 800;
        }


        .md-refresh-btn {
          background: #ffffff;

          color: #334e68;

          box-shadow:
            0 4px 16px
            rgba(
              16,
              42,
              67,
              0.08
            );
        }


        .md-excel-btn {
          background: #15803d;

          color: #ffffff;
        }


        .md-excel-btn:hover {
          background: #166534;
        }


        .md-refresh-btn:disabled,
        .md-excel-btn:disabled {
          opacity: 0.5;

          cursor: not-allowed;
        }


        .md-spin {
          animation:
            mdSpin
            0.8s
            linear
            infinite;
        }


        @keyframes mdSpin {

          to {

            transform:
              rotate(360deg);

          }

        }


        /* ====================================================
           SESSION
        ==================================================== */

        .md-session-card {
          min-height: 64px;

          padding: 13px 17px;

          display: flex;

          align-items: center;

          gap: 13px;

          margin-bottom: 18px;

          box-sizing: border-box;

          background: #ffffff;

          border:
            1px solid #e5edf5;

          border-radius: 13px;
        }


        .md-session-icon {
          width: 39px;
          height: 39px;

          flex: 0 0 39px;

          display: grid;

          place-items: center;

          border-radius: 9px;

          background: #ecfeff;

          color: #0891b2;
        }


        .md-session-card span {
          color: #829ab1;

          font-size: 10px;
        }


        .md-session-card h3 {
          margin: 3px 0 0;

          color: #102a43;

          font-size: 15px;
        }


        .md-session-date {
          margin-left: auto;

          display: flex;

          align-items: center;

          gap: 9px;

          color: #627d98;

          font-size: 11px;
        }


        /* ====================================================
           SUMMARY
        ==================================================== */

        .md-summary-grid {
          display: grid;

          grid-template-columns:
            repeat(
              3,
              minmax(0, 1fr)
            );

          gap: 14px;

          margin-bottom: 18px;
        }


        .md-summary-card {
          min-height: 76px;

          padding: 15px 17px;

          display: flex;

          align-items: center;

          gap: 14px;

          box-sizing: border-box;

          background: #ffffff;

          border:
            1px solid #e7eef5;

          border-radius: 13px;

          box-shadow:
            0 5px 18px
            rgba(
              16,
              42,
              67,
              0.04
            );
        }


        .md-summary-card-icon {
          width: 44px;
          height: 44px;

          flex: 0 0 44px;

          display: grid;

          place-items: center;

          border-radius: 11px;

          font-size: 17px;
        }


        .md-summary-card-icon.red {
          background: #fee2e2;

          color: #dc2626;
        }


        .md-summary-card-icon.blue {
          background: #e0f2fe;

          color: #0284c7;
        }


        .md-summary-card-icon.purple {
          background: #f3e8ff;

          color: #9333ea;
        }


        .md-summary-card span {
          display: block;

          color: #829ab1;

          font-size: 9px;

          font-weight: 800;

          text-transform:
            uppercase;
        }


        .md-summary-card h2 {
          margin: 5px 0 0;

          color: #102a43;

          font-size: 19px;

          font-weight: 900;
        }


        .summary-red-text {
          color: #dc2626 !important;
        }


        /* ====================================================
           REPORT
        ==================================================== */

        .md-report-card {
          overflow: hidden;

          background: #ffffff;

          border:
            1px solid #e7eef5;

          border-radius: 14px;

          box-shadow:
            0 5px 22px
            rgba(
              16,
              42,
              67,
              0.05
            );
        }


        .md-card-header {
          min-height: 70px;

          padding: 15px 19px;

          box-sizing: border-box;

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 18px;

          border-bottom:
            1px solid #edf2f7;
        }


        .md-card-header h2 {
          margin: 0;

          color: #102a43;

          font-size: 17px;

          font-weight: 850;
        }


        .md-card-header p {
          margin: 4px 0 0;

          color: #829ab1;

          font-size: 10px;
        }


        .md-header-total {
          text-align: right;
        }


        .md-header-total span {
          display: block;

          color: #829ab1;

          font-size: 9px;

          font-weight: 800;

          text-transform:
            uppercase;
        }


        .md-header-total strong {
          display: block;

          margin-top: 3px;

          color: #dc2626;

          font-size: 19px;

          font-weight: 900;
        }


        /* ====================================================
           TABLE
        ==================================================== */

        .md-table-wrap {
          width: 100%;

          overflow-x: auto;
        }


        .md-table {
          width: 100%;

          min-width: 1000px;

          border-collapse:
            collapse;

          border-spacing: 0;
        }


        .md-table th {
          padding: 12px 16px;

          background: #f8fafc;

          border-bottom:
            1px solid #e5edf5;

          color: #627d98;

          font-size: 12px;

          font-weight: 850;

          text-transform:
            uppercase;

          letter-spacing:
            0.04em;

          white-space: nowrap;

          text-align: left;
        }


        .md-table td {
          padding: 13px 16px;

          border-bottom:
            1px solid #edf2f7;

          color: #334e68;

          font-size: 12px;

          vertical-align: middle;
        }


        .md-table tbody tr:hover {
          background: #f8fbfd;
        }


        .text-center {
          text-align:
            center !important;
        }


        .text-right {
          text-align:
            right !important;
        }


        /* ====================================================
           MONTH
        ==================================================== */

        .md-month {
          display: flex;

          align-items: center;

          gap: 11px;
        }


        .md-month-box {
          width: 40px;
          height: 40px;

          flex: 0 0 40px;

          display: grid;

          place-items: center;

          border-radius: 9px;

          background: #edf8ff;

          color: #0369a1;

          font-size: 10px;

          font-weight: 900;
        }


        .md-month strong {
          display: block;

          color: #102a43;

          font-size: 14px;

          font-weight: 850;
        }


        .md-month span {
          display: block;

          margin-top: 2px;

          color: #9fb3c8;

          font-size: 9px;
        }


        /* ====================================================
           STUDENT BADGE
        ==================================================== */

        .md-student-badge {
          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 5px;

          min-width: 47px;

          padding: 6px 9px;

          box-sizing: border-box;

          border-radius: 20px;

          background: #eff6ff;

          color: #2563eb;

          font-size: 10px;

          font-weight: 850;
        }


        /* ====================================================
           MONEY
        ==================================================== */

        .md-payable-value {
          color: #102a43;

          font-size: 14px;

          font-weight: 850;
        }


        .md-collected-value {
          color: #15803d;

          font-size: 14px;

          font-weight: 900;
        }


        .md-due-amount {
          color: #dc2626;

          font-size: 14px;

          font-weight: 900;
        }


        .md-zero {
          color: #16a34a;

          font-size: 14px;

          font-weight: 800;
        }


        .md-upcoming {
          color: #94a3b8;

          font-size: 14px;
        }


        /* ====================================================
           VIEW
        ==================================================== */

        .md-view-btn {
          border: 0;

          outline: none;

          padding: 7px 13px;

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 6px;

          border-radius: 7px;

          background: #102a43;

          color: #ffffff;

          cursor: pointer;

          font-size: 10px;

          font-weight: 800;
        }


        .md-view-btn:hover {
          background: #155e75;
        }


        .md-view-btn:disabled {
          background: #aab7c2;

          opacity: 1;

          cursor: not-allowed;
        }


        .md-future-row {
          background: #fbfdff;
        }


        /* ====================================================
           GRAND TOTAL ROW
           TABLE-ER LAST LINE
        ==================================================== */

        .md-table tfoot td {
          padding: 16px;

          border: 0;

          border-top:
            3px solid #0b2942;

          background: #102a43;

          color: #ffffff;

          vertical-align: middle;
        }


        .md-grand-title {
          color:
            #ffffff !important;

          font-size: 13px !important;

          font-weight: 900 !important;

          letter-spacing:
            0.03em;
        }


        .md-grand-title small {
          display: block;

          margin-top: 3px;

          color: #9cc7df;

          font-size: 8px;

          font-weight: 600;
        }


        .grand-payable-value {
          color: #ffffff;

          font-size: 14px;

          font-weight: 900;

          white-space: nowrap;
        }


        .grand-collected-value {
          color: #86efac;

          font-size: 14px;

          font-weight: 900;

          white-space: nowrap;
        }


        .grand-due-value {
          color: #fecaca;

          font-size: 15px;

          font-weight: 900;

          white-space: nowrap;
        }


        .grand-label-mobile {
          display: none;
        }


        /* ====================================================
           ERROR / LOADING
        ==================================================== */

        .md-error {
          margin-bottom: 15px;

          padding: 12px 14px;

          border-radius: 9px;

          background: #fff1f2;

          color: #be123c;

          font-size: 11px;
        }


        .md-loading {
          min-height: 240px;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          gap: 11px;

          color: #829ab1;

          font-size: 11px;
        }


        .md-loader {
          width: 31px;
          height: 31px;

          border:
            3px solid #dbeafe;

          border-top-color:
            #2563eb;

          border-radius: 50%;

          animation:
            mdSpin
            0.8s
            linear
            infinite;
        }


        .md-empty {
          padding:
            50px !important;

          text-align:
            center !important;

          color:
            #94a3b8 !important;
        }


        /* ====================================================
           RESPONSIVE
        ==================================================== */

        @media (
          max-width: 900px
        ) {

          .md-page {
            padding: 14px;
          }


          .md-page-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }


          .md-summary-grid {
            grid-template-columns:
              1fr;
          }


          .md-session-date {
            display: none;
          }


          .md-card-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }


          .md-header-total {
            text-align: left;
          }

        }

      `}</style>


    </div>
  );
};


export default MonthlyDueReportPage;